import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Money, ArrowClockwise, Check, SpinnerGap, Clock, FileText, Minus, Warning, PaperPlaneTilt, Trash } from '@phosphor-icons/react';
import { EmployeeName } from '@/components/EmployeeName';
import { toast } from 'sonner';

import OvertimeTab from './payroll/OvertimeTab';
import DeductionsTab from './payroll/DeductionsTab';
import SalaryAdjustmentDialog from './payroll/SalaryAdjustmentDialog';
import AttendanceCorrectionDialog from './payroll/AttendanceCorrectionDialog';
import ScrollShadow from '@/components/ScrollShadow';
import { formatHoursHM, formatHoursHMLabeled } from '@/utils/formatHours';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function AdminPayroll() {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const { empNo } = useEmployeesMap();
  const { getBadge } = useEmployeeBadges();
  const navigate = useNavigate();

  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  // v1.6.12: Secondary modal shown after the admin confirms the
  // cycle. Lists employees who have at least one approved annual
  // leave inside the cycle so finance can pick which ones already
  // received their leave salary as a travel advance (→ deduct from
  // this payroll) vs which ones will be paid with the regular salary.
  const [annualLeavePreview, setAnnualLeavePreview] = useState(null);  // { employees: [...] } | null
  const [advancePaidIds, setAdvancePaidIds] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(5, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('payroll');
  const [cycle, setCycle] = useState(null);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [showAttCorrectionDialog, setShowAttCorrectionDialog] = useState(false);
  // v1.6.8: On first mount we open the Payroll tab on the most recent
  // DRAFT cycle (falling back to the most recent cycle regardless of
  // status), so finance never has to scroll past old cycles. The
  // auto-select runs exactly once.
  const [didAutoSelectCycle, setDidAutoSelectCycle] = useState(false);

  useEffect(() => { fetchPayroll(); }, [filterMonth, filterYear]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/config/public`, { withCredentials: true });
        setCycle({ start: data.payroll_cycle_start_day, end: data.payroll_cycle_end_day });
      } catch { /* config endpoint is best-effort */ }
    })();
  }, []);

  // Employees list is used by BOTH the Overtime and Deductions tabs.
  // Fetch it once when the page mounts so switching tabs doesn't leave
  // any picker empty.
  useEffect(() => { fetchEmployees(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      let url = `${API}/payroll`;
      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);
      if (params.toString()) url += `?${params.toString()}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setPayroll(data);
      // v1.6.8: First time the full list arrives (no filter applied),
      // detect the most recent cycle that still has at least one draft
      // record and auto-select it. If no draft exists, fall back to the
      // most recent cycle so finance opens exactly on the active sheet.
      if (!didAutoSelectCycle && !filterMonth && !filterYear && Array.isArray(data) && data.length > 0) {
        const sortKey = (r) => `${r.year}-${String(r.month).padStart(2, '0')}`;
        const sorted = [...data].sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
        // v1.6.10: the DB stores drafts with `status: 'pending'` (NOT
        // 'draft') and sets `published_at` only after Publish. The UI's
        // single source of truth for "draft" is therefore `!published_at`.
        const latestDraft = sorted.find(r => !r.published_at) || sorted[0];
        if (latestDraft) {
          setDidAutoSelectCycle(true);
          setFilterYear(String(latestDraft.year));
          setFilterMonth(String(latestDraft.month).padStart(2, '0'));
          // The filter change will trigger a refetch, so stop here.
          return;
        }
      }
    } catch (error) {
      console.error('Failed to fetch payroll:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
      setEmployees(data);
    } catch { /* silent — employee dropdown stays empty on error */ }
  };

  const handleGenerate = async () => {
    // v1.6.12: Two-step generation flow.
    //   Step 1 — look up every approved annual leave inside the cycle.
    //   Step 2 — if any exist, open the "paid in advance?" picker so
    //            finance can choose per-employee. Otherwise proceed
    //            straight to the real generation (no deductions).
    setGenerating(true);
    try {
      const { data } = await axios.get(
        `${API}/payroll/annual-leaves-preview?month=${selectedMonth}&year=${selectedYear}`,
        { withCredentials: true },
      );
      const emps = data?.employees || [];
      if (emps.length === 0) {
        await _runGeneratePayroll([]);
        return;
      }
      setAnnualLeavePreview(data);
      setAdvancePaidIds(new Set());  // default: nobody is advance-paid
      setShowGenerateDialog(false);
    } catch (error) {
      console.error('Failed to fetch annual-leave preview:', error);
      // On preview failure, fall back to the regular generation path.
      await _runGeneratePayroll([]);
    } finally {
      setGenerating(false);
    }
  };

  const _runGeneratePayroll = async (advanceIds) => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/payroll/generate?month=${selectedMonth}&year=${selectedYear}`,
        { advance_paid_user_ids: advanceIds },
        { withCredentials: true },
      );
      toast.success(language === 'ar'
        ? 'تم توليد كشف الرواتب كمسودة — راجع البيانات ثم اضغط "إرسال للموظفين"'
        : 'Payroll generated as draft — review then click "Publish"');
      // Surface the early-cycle warning so finance knows recomputation
      // will happen at publish time.
      const warn = data?.early_cycle_warning;
      if (warn) {
        toast.warning(language === 'ar' ? warn.message_ar : warn.message_en, {
          duration: 12000,
        });
      }
      setShowGenerateDialog(false);
      setAnnualLeavePreview(null);
      setAdvancePaidIds(new Set());
      // v1.6.10: Setting the filters triggers the `useEffect` watcher
      // which refetches with the correct month/year. Previously we also
      // called `fetchPayroll()` directly on the next line, which raced
      // the useEffect fetch using the OLD (empty) filter values — if
      // the unfiltered response arrived last, the table rendered rows
      // from other cycles together with October. Dropping the manual
      // call eliminates the race.
      setFilterMonth(selectedMonth);
      setFilterYear(selectedYear.toString());
    } catch (error) {
      console.error('Failed to generate payroll:', error);
      // v1.6.9: `existing_draft_blocks_generation` returns a structured
      // 409 body with Arabic + English messages. Prefer those over the
      // plain `detail` fallback used by other errors.
      const d = error.response?.data?.detail;
      const msg = (d && typeof d === 'object' && (language === 'ar' ? d.message_ar : d.message_en))
        || (typeof d === 'string' ? d : null)
        || (language === 'ar' ? 'تعذّر توليد المسير' : 'Failed to generate payroll');
      toast.error(msg, { duration: 10000 });
    } finally {
      setGenerating(false);
    }
  };

  const [publishing, setPublishing] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  // Preview dialog state — populated with the response from
  // `POST /payroll/publish?dry_run=true`. When `previewOpen=true` the
  // admin sees a modal listing every affected employee (before/after net)
  // + a "تأكيد الإرسال" button that triggers the real (persisting) call.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);   // { ids: [...], recomputed: [...], mode: 'one'|'all' }
  const [previewLoading, setPreviewLoading] = useState(false);

  // ─── Preview + real publish orchestration ────────────────────────────
  // Every publish flow now runs a `dry_run=true` first. If any record
  // would change, we open the preview modal and defer the real publish
  // until the admin confirms. If nothing would change, we skip the
  // modal and publish immediately.
  const runPublish = async (ids, opts = {}) => {
    const { silent = false } = opts;
    const r = await axios.post(`${API}/payroll/publish`, {
      payroll_ids: ids,
      dry_run: false,
    }, { withCredentials: true });
    if (!silent) {
      const label = ids.length === 1
        ? (language === 'ar' ? 'تم الإرسال' : 'Sent')
        : (language === 'ar' ? `تم الإرسال إلى ${r.data.sent} موظف` : `Published to ${r.data.sent}`);
      toast.success(label);
    }
    fetchPayroll();
    return r.data;
  };

  const openPublishPreview = async (ids, mode) => {
    if (!ids || ids.length === 0) {
      toast.info(language === 'ar' ? 'لا توجد كشوف للإرسال' : 'Nothing to send');
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await axios.post(`${API}/payroll/publish`, {
        payroll_ids: ids,
        dry_run: true,
      }, { withCredentials: true });
      const recomputed = r.data?.recomputed || [];
      if (recomputed.length === 0) {
        // No adjustments needed — publish directly.
        await runPublish(ids);
      } else {
        setPreviewData({ ids, mode, recomputed });
        setPreviewOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmPublishFromPreview = async () => {
    if (!previewData) return;
    setPublishing(true);
    try {
      await runPublish(previewData.ids);
      toast.info(language === 'ar'
        ? `📊 تم تحديث ${previewData.recomputed.length} كشف قبل الإرسال`
        : `📊 ${previewData.recomputed.length} record(s) auto-adjusted before publishing`,
        { duration: 6000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
      setPreviewOpen(false);
      setPreviewData(null);
    }
  };

  // Publish a SINGLE payroll record — the admin uses this when they've
  // reviewed one employee at a time and want to release only their
  // payslip. Runs the preview flow first so recomputed nets are shown
  // before the release actually happens.
  const handlePublishOne = async (record) => {
    if (record.published_at) {
      toast.info(language === 'ar' ? 'هذا الكشف مُرسَل بالفعل' : 'Already sent');
      return;
    }
    const empName = record.user_name || record.user_name_en || '';
    if (!window.confirm(language === 'ar'
      ? `سيتم إرسال كشف الراتب لـ ${empName}. متابعة؟`
      : `Will send payslip to ${empName}. Continue?`)) return;
    setPublishingId(record.id);
    try {
      await openPublishPreview([record.id], 'one');
    } finally {
      setPublishingId(null);
    }
  };

  const handlePublishAll = async () => {
    const unpublished = payroll.filter((r) => !r.published_at);
    if (unpublished.length === 0) {
      toast.info(language === 'ar' ? 'كل الكشوف مُرسلة بالفعل' : 'All records already published');
      return;
    }
    if (!window.confirm(language === 'ar'
      ? `سيتم فحص ${unpublished.length} كشف قبل الإرسال. متابعة؟`
      : `Will preview ${unpublished.length} record(s) before sending. Continue?`)) return;
    setPublishing(true);
    try {
      await openPublishPreview(unpublished.map((x) => x.id), 'all');
    } finally {
      setPublishing(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  // Export the entire cycle to XLSX or PDF. The backend picks up whatever
  // month/year are currently filtered — if the admin hasn't filtered, fall
  // back to the selectedMonth/selectedYear used for generation. Uses a
  // blob response + hidden <a> download so the file streams straight to
  // disk without navigating away.
  const handleExportCycle = async (format /* 'xlsx' | 'pdf' */) => {
    const m = filterMonth || selectedMonth;
    const y = filterYear || selectedYear;
    if (!m || !y) {
      toast.error(language === 'ar' ? 'اختر الشهر والسنة أولاً' : 'Pick month and year first');
      return;
    }
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/payroll/export`, {
        params: { year: y, month: m, format },
        responseType: 'blob',
        withCredentials: true,
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([resp.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_${y}_${String(m).padStart(2, '0')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم تنزيل الملف' : 'File downloaded');
    } catch (error) {
      let detail = error.response?.data?.detail;
      // Blob-error response — need to read it
      if (!detail && error.response?.data instanceof Blob) {
        try {
          const txt = await error.response.data.text();
          detail = JSON.parse(txt)?.detail;
        } catch (_) { /* ignore */ }
      }
      toast.error(detail || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleMarkPaid = async (payrollId) => {
    try {
      await axios.put(`${API}/payroll/${payrollId}/pay`, {}, { withCredentials: true });
      fetchPayroll();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
    }
  };

  // Delete a single payroll record — draft-only by default. Published
  // rows require an explicit confirmation because the employee has
  // already been notified/seen the figure.
  const [deletingId, setDeletingId] = useState(null);
  const handleDeleteOne = async (record) => {
    const isPublished = !!record.published_at;
    const empName = record.user_name || record.user_name_en || '';
    const warn = isPublished
      ? (language === 'ar'
          ? `⚠️ هذا الكشف مُرسَل بالفعل للموظف "${empName}". حذفه سيحرمه من رؤيته. هل أنت متأكد تماماً؟`
          : `⚠️ This payslip has already been sent to "${empName}". Deleting it will hide it from them. Continue?`)
      : (language === 'ar'
          ? `سيتم حذف كشف راتب "${empName}" (مسودة). متابعة؟`
          : `Draft payslip for "${empName}" will be deleted. Continue?`);
    if (!window.confirm(warn)) return;
    setDeletingId(record.id);
    try {
      const url = isPublished
        ? `${API}/payroll/${record.id}?force=true`
        : `${API}/payroll/${record.id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم حذف الكشف' : 'Deleted');
      fetchPayroll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk-delete all DRAFTS for the currently selected cycle (safety:
  // published rows are preserved unless the admin explicitly opts in).
  const handleDeleteCycleDrafts = async () => {
    const drafts = payroll.filter((r) => !r.published_at);
    if (drafts.length === 0) {
      toast.info(language === 'ar' ? 'لا توجد مسودات للحذف' : 'No drafts to delete');
      return;
    }
    // Use the cycle from the first draft so we hit the right month/year
    // even when the filter dropdowns are empty (initial "current cycle").
    const m = drafts[0].month;
    const y = drafts[0].year;
    if (!window.confirm(language === 'ar'
      ? `سيتم حذف ${drafts.length} مسودة راتب لدورة ${m}/${y}. متابعة؟`
      : `Will delete ${drafts.length} draft payslip(s) for ${m}/${y}. Continue?`)) return;
    try {
      const r = await axios.delete(`${API}/payroll/cycle/${y}/${m}`, { withCredentials: true });
      toast.success(language === 'ar'
        ? `تم حذف ${r.data.deleted} مسودة`
        : `Deleted ${r.data.deleted} draft(s)`);
      fetchPayroll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  const handleDownloadPayslip = async (payrollId) => {
    try {
      const response = await axios.get(`${API}/payroll/${payrollId}/payslip`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(language === 'ar' ? 'تم تحميل كشف الراتب' : 'Payslip downloaded');
    } catch { toast.error(language === 'ar' ? 'فشل التحميل' : 'Download failed'); }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      paid: 'bg-green-100 text-green-700 border-green-200'
    };
    return variants[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const months = [
    { value: '01', label: t('months.01') }, { value: '02', label: t('months.02') },
    { value: '03', label: t('months.03') }, { value: '04', label: t('months.04') },
    { value: '05', label: t('months.05') }, { value: '06', label: t('months.06') },
    { value: '07', label: t('months.07') }, { value: '08', label: t('months.08') },
    { value: '09', label: t('months.09') }, { value: '10', label: t('months.10') },
    { value: '11', label: t('months.11') }, { value: '12', label: t('months.12') },
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="admin-payroll-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6]">{t('payroll')}</h1>

        {cycle && (() => {
          const today = new Date().getDate();
          const inWindow = today >= cycle.start && today <= cycle.end;
          return (
            <div
              className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border ${
                inWindow
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-[#F2F0FA] border-[#C4BFF0]'
              }`}
              data-testid="payroll-cycle-banner"
            >
              <div className="flex items-center gap-3">
                <span className={`text-2xl ${inWindow ? '' : 'opacity-60'}`}>{inWindow ? '🟢' : '📅'}</span>
                <div>
                  <p className={`text-sm font-semibold ${inWindow ? 'text-emerald-800' : 'text-[#655dc6]'}`}>
                    {language === 'ar'
                      ? (inWindow ? 'نحن داخل نافذة صرف الرواتب' : 'دورة صرف الرواتب')
                      : (inWindow ? 'Currently inside the payroll window' : 'Payroll cycle')}
                  </p>
                  <p className={`text-xs ${inWindow ? 'text-emerald-700/80' : 'text-[#655dc6]/70'}`}>
                    {language === 'ar'
                      ? `من يوم ${cycle.start} إلى يوم ${cycle.end} من كل شهر — اليوم ${today}`
                      : `Day ${cycle.start} → ${cycle.end} of each month — today: ${today}`}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={() => navigate('/admin/settings')}
                className={`text-xs ${inWindow ? 'text-emerald-700 hover:bg-emerald-100' : 'text-[#655dc6] hover:bg-[#E8E6F5]'}`}
                data-testid="cycle-edit-btn"
              >
                {language === 'ar' ? 'تعديل الدورة' : 'Edit cycle'}
              </Button>
            </div>
          );
        })()}

        <Tabs value={activeTab} onValueChange={setActiveTab} dir={direction}>
          <TabsList className="bg-[#E8E6F5] border border-[#C4BFF0] p-1 h-auto w-full sm:w-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="payroll" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white px-6 py-2 gap-2 shrink-0" data-testid="payroll-tab">
              <Money size={18} weight="duotone" />
              {t('payroll_tab')}
            </TabsTrigger>
            <TabsTrigger value="overtime" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white px-6 py-2 gap-2 shrink-0" data-testid="overtime-tab">
              <Clock size={18} weight="duotone" />
              {t('overtime_tab')}
            </TabsTrigger>
            <TabsTrigger value="deductions" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white px-6 py-2 gap-2 shrink-0" data-testid="deductions-tab">
              <Minus size={18} weight="duotone" />
              {language === 'ar' ? 'الخصومات' : 'Deductions'}
            </TabsTrigger>
          </TabsList>

          {/* ===== PAYROLL TAB ===== */}
          <TabsContent value="payroll" className="space-y-4 mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex flex-wrap gap-3">
                <Select value={filterMonth} onValueChange={setFilterMonth}>
                  <SelectTrigger className="w-[150px] bg-[#E8E6F5] border-[#C4BFF0]" data-testid="filter-month">
                    <SelectValue placeholder={t('select_month')} />
                  </SelectTrigger>
                  <SelectContent>{months.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                </Select>
                <Select value={filterYear} onValueChange={setFilterYear}>
                  <SelectTrigger className="w-[120px] bg-[#E8E6F5] border-[#C4BFF0]" data-testid="filter-year">
                    <SelectValue placeholder={t('select_year')} />
                  </SelectTrigger>
                  <SelectContent>{years.map((y) => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}</SelectContent>
                </Select>
                <Button variant="outline" onClick={() => { setFilterMonth(''); setFilterYear(''); }} className="border-[#C4BFF0]">
                  {t('cancel')}
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => handleExportCycle('xlsx')}
                  disabled={exporting || payroll.length === 0}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  data-testid="export-payroll-xlsx-btn"
                  title={language === 'ar' ? 'تصدير المسير كملف Excel' : 'Export payroll as Excel'}
                >
                  <FileText size={18} className="me-2" />
                  {language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
                </Button>
                <Button
                  onClick={() => handleExportCycle('pdf')}
                  disabled={exporting || payroll.length === 0}
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                  data-testid="export-payroll-pdf-btn"
                  title={language === 'ar' ? 'تصدير المسير كملف PDF' : 'Export payroll as PDF'}
                >
                  <FileText size={18} className="me-2" />
                  {language === 'ar' ? 'تصدير PDF' : 'Export PDF'}
                </Button>
                <Button
                  onClick={handleDeleteCycleDrafts}
                  disabled={payroll.filter((r) => !r.published_at).length === 0}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                  data-testid="delete-cycle-drafts-btn"
                  title={language === 'ar' ? 'حذف جميع المسودات لهذه الدورة' : 'Delete all drafts for this cycle'}
                >
                  <Trash size={18} className="me-2" />
                  {language === 'ar'
                    ? `حذف المسودات (${payroll.filter((r) => !r.published_at).length})`
                    : `Delete Drafts (${payroll.filter((r) => !r.published_at).length})`}
                </Button>
                <Button
                  onClick={handlePublishAll}
                  disabled={publishing || payroll.filter((r) => !r.published_at).length === 0}
                  variant="outline"
                  className="border-[#655dc6] text-[#655dc6] hover:bg-[#EDE9FE]"
                  data-testid="publish-payroll-btn"
                  title={language === 'ar' ? 'إرسال كشوف الرواتب للموظفين عبر الإيميل والإشعارات' : 'Send payslips to employees via email + notifications'}
                >
                  {publishing ? <SpinnerGap size={18} className="me-2 animate-spin" /> : <Check size={18} className="me-2" />}
                  {language === 'ar' ? `إرسال الكل (${payroll.filter((r) => !r.published_at).length})` : `Send All (${payroll.filter((r) => !r.published_at).length})`}
                </Button>
                <Button
                  onClick={() => setShowAttCorrectionDialog(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="att-correction-btn"
                  title={language === 'ar' ? 'تصحيح بصمة يوم على قسيمة مُصدَرة' : 'Correct attendance on issued payslip'}
                >
                  <FileText size={18} className="me-2" />
                  {language === 'ar' ? 'تصحيح بصمة' : 'Attendance fix'}
                </Button>
                <Button
                  onClick={() => setShowAdjustmentDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="adjustment-btn"
                  title={language === 'ar' ? 'إصدار قسيمة تعديل راتب (زيادة / تصحيح)' : 'Issue salary adjustment payslip (raise / correction)'}
                >
                  <FileText size={18} className="me-2" />
                  {language === 'ar' ? 'تعديل راتب' : 'Adjustment'}
                </Button>
                <Button onClick={() => setShowGenerateDialog(true)} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="generate-payroll-btn">
                  <ArrowClockwise size={20} className="me-2" />
                  {t('generate_payroll')}
                </Button>
              </div>
            </div>

            <Card className="border border-[#C4BFF0]">
              <CardContent className="pt-6">
                {/* Cycle-review banner — shows when at least one record was
                    prorated because the employee didn't cover the full
                    cycle. The admin should verify these before publishing.
                    Static + not dismissible on purpose: this is a payroll
                    audit signal, not a marketing hint. */}
                {(() => {
                  const proratedRows = payroll.filter((r) => r.prorated);
                  if (!proratedRows.length) return null;
                  return (
                    <div
                      className="mb-4 rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800/50 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2"
                      data-testid="payroll-prorated-alert"
                    >
                      <Warning size={18} weight="fill" className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                      <div className="flex-1">
                        <p className="font-semibold">
                          {language === 'ar'
                            ? `يوجد ${proratedRows.length} موظف باشر بعد بدء الدورة — راجع رواتبهم يدوياً`
                            : `${proratedRows.length} employee(s) started after the cycle began — please review their salaries manually`}
                        </p>
                        <p className="text-xs opacity-80 mt-0.5">
                          {language === 'ar'
                            ? 'تم احتساب الراتب الأساسي والبدلات تناسبياً بناءً على تاريخ التعيين. يمكنك تعديل السجل قبل الإرسال.'
                            : 'Basic salary and allowances were prorated based on hire date. You can edit each record before publishing.'}
                        </p>
                      </div>
                    </div>
                  );
                })()}
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
                  </div>
                ) : (
                  <>
                  {(() => {
                    const sortedPayroll = [...payroll].sort((a, b) => {
                      const raw = (v) => {
                        const s = String(empNo(v.user_id) || '').trim();
                        return (s === '' || s === '—' || s === '-') ? '' : s;
                      };
                      const na = raw(a);
                      const nb = raw(b);
                      if (!na && !nb) return 0;
                      if (!na) return 1;
                      if (!nb) return -1;
                      return na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
                    });
                    return (
                      <>
                      {/* Mobile Card List (<640px) */}
                      <div className="sm:hidden space-y-3" data-testid="payroll-mobile-list">
                        {sortedPayroll.length === 0 ? (
                          <p className="text-center py-8 text-[#655dc6]/70 text-sm">{t('no_data')}</p>
                        ) : (
                          sortedPayroll.map((record) => {
                            const gross = (record.basic_salary || 0) + (record.allowances || 0);
                            const penalty = (record.late_penalty || 0) + (record.unpaid_leave_deduction || 0) + (record.unexcused_absence_deduction || 0) + (record.advance_paid_leave_deduction || 0);
                            const workedDays = (() => {
                              if (record.attendance_days_actual !== undefined && record.attendance_days_actual !== null) {
                                return Number(record.attendance_days_actual);
                              }
                              const total = record.worked_days_in_cycle || record.cycle_total_days || 0;
                              const absent = (record.unpaid_leave_days || 0) + (record.unexcused_absence_days || 0) + (record.paid_leave_days || 0);
                              return Math.max(0, total - absent);
                            })();
                            return (
                              <div
                                key={record.id}
                                data-testid={`payroll-card-${record.id}`}
                                className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                              >
                                {/* Header */}
                                <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                                  <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-[#262263] dark:text-slate-100 text-sm">
                                      <EmployeeName name={record.user_name} nameEn={record.user_name_en} badgeData={getBadge(record.user_id)} badgeLanguage={language} />
                                    </div>
                                    <div className="text-[11px] text-[#655dc6]/70 dark:text-slate-400 font-mono mt-0.5">
                                      #{empNo(record.user_id)} · {record.month}/{record.year}
                                    </div>
                                    {record.prorated && (
                                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                        <Warning size={10} weight="fill" />
                                        {language === 'ar' ? 'محسوب تناسبياً' : 'Prorated'}
                                      </div>
                                    )}
                                  </div>
                                  {record.published_at ? (
                                    <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] px-2 py-0.5 shrink-0" data-testid={`status-mobile-${record.id}`}>
                                      {language === 'ar' ? 'مُرسَل ✓' : 'Sent ✓'}
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-[10px] px-2 py-0.5 shrink-0" data-testid={`status-mobile-${record.id}`}>
                                      {language === 'ar' ? 'مسودة' : 'Draft'}
                                    </Badge>
                                  )}
                                </div>

                                {/* Earnings */}
                                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                  <div>
                                    <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{language === 'ar' ? 'أيام العمل' : 'Work Days'}</span>
                                    <span className="font-semibold text-slate-800 dark:text-slate-200">{workedDays}</span>
                                  </div>
                                  <div>
                                    <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('basic_salary')}</span>
                                    <span className="font-semibold text-slate-800 dark:text-slate-200">{record.basic_salary?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{language === 'ar' ? 'البدلات' : 'Allowances'}</span>
                                    <span className="font-semibold text-green-600">+{(record.allowances || 0).toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{language === 'ar' ? 'الإجمالي' : 'Gross'}</span>
                                    <span className="font-semibold text-[#655dc6]">{gross.toLocaleString()}</span>
                                  </div>
                                  {(record.overtime_amount || 0) > 0 && (
                                    <div>
                                      <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('overtime_amount')}</span>
                                      <span className="font-semibold text-blue-600">+{(record.overtime_amount || 0).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {(record.gosi_subscription || 0) > 0 && (
                                    <div>
                                      <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">GOSI</span>
                                      <span className="font-semibold text-rose-700">-{record.gosi_subscription.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {penalty > 0 && (
                                    <div>
                                      <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{language === 'ar' ? 'خصم جزائي' : 'Penalty'}</span>
                                      <span className="font-semibold text-rose-700">-{penalty.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {(record.manual_deductions_total || 0) > 0 && (
                                    <div>
                                      <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{language === 'ar' ? 'خصم إداري' : 'Admin'}</span>
                                      <span className="font-semibold text-rose-700">-{record.manual_deductions_total.toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Net + actions */}
                                <div className="pt-3 flex items-center justify-between gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                                  <div>
                                    <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-500 uppercase tracking-wide">{t('net_salary')}</span>
                                    <span className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300" data-testid={`net-salary-mobile-${record.id}`}>
                                      {record.net_salary?.toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {!record.published_at && (
                                      <Button
                                        size="sm"
                                        onClick={() => handlePublishOne(record)}
                                        disabled={publishingId === record.id}
                                        className="bg-blue-600 hover:bg-blue-700 text-white min-h-[44px] px-3 gap-1"
                                        data-testid={`send-one-btn-mobile-${record.id}`}
                                      >
                                        {publishingId === record.id ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
                                        <span className="text-xs">{language === 'ar' ? 'إرسال' : 'Send'}</span>
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDownloadPayslip(record.id)}
                                      className="min-h-[44px] px-3 border-[#C4BFF0] text-[#655dc6] gap-1"
                                      data-testid={`payslip-btn-mobile-${record.id}`}
                                    >
                                      <FileText size={14} />
                                      <span className="text-xs">{language === 'ar' ? 'كشف' : 'Slip'}</span>
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => handleDeleteOne(record)}
                                      disabled={deletingId === record.id}
                                      className="h-11 w-11 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`delete-payroll-mobile-${record.id}`}
                                    >
                                      {deletingId === record.id ? <SpinnerGap size={16} className="animate-spin" /> : <Trash size={16} />}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Desktop Table (>=640px) */}
                      <div className="hidden sm:block">
                      <ScrollShadow minChildWidth={1400}>
                    <Table data-testid="payroll-table" className="[&_th]:text-center [&_td]:text-center [&_th]:align-middle [&_td]:align-middle text-xs">
                      <TableHeader>
                        <TableRow className="border-[#C4BFF0]">
                          <TableHead className="text-start">{t('employee_number')}</TableHead>
                          <TableHead className="text-start">{t('employee_name')}</TableHead>
                          <TableHead className="text-start">{language === 'ar' ? 'أيام العمل' : 'Work Days'}</TableHead>
                          <TableHead className="text-start">{t('select_month')}</TableHead>
                          <TableHead className="text-start">{t('basic_salary')}</TableHead>
                          <TableHead className="text-start text-green-700">{language === 'ar' ? 'بدل السكن' : 'Housing'}</TableHead>
                          <TableHead className="text-start text-green-700">{language === 'ar' ? 'بدل المواصلات' : 'Transport'}</TableHead>
                          <TableHead className="text-start text-green-700">{language === 'ar' ? 'بدلات أخرى' : 'Other'}</TableHead>
                          <TableHead className="text-start bg-[#EDE9FE] dark:bg-[#655dc6]/20 font-semibold">
                            {language === 'ar' ? 'الراتب الإجمالي' : 'Gross Salary'}
                          </TableHead>
                          <TableHead className="text-start">{t('overtime_amount')}</TableHead>
                          <TableHead className="text-start text-rose-700">{language === 'ar' ? 'خصم GOSI' : 'GOSI'}</TableHead>
                          <TableHead className="text-start text-rose-700">{language === 'ar' ? 'خصم جزائي' : 'Penalty'}</TableHead>
                          <TableHead className="text-start text-rose-700">{language === 'ar' ? 'خصم إداري' : 'Admin'}</TableHead>
                          <TableHead className="text-start bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-200 font-semibold">
                            {t('net_salary')}
                          </TableHead>
                          <TableHead className="text-start">{t('status')}</TableHead>
                          <TableHead className="text-start">{t('actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payroll.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={16} className="text-center py-8 text-[#655dc6]/70">{t('no_data')}</TableCell>
                          </TableRow>
                        ) : (
                          [...payroll].sort((a, b) => {
                            // Ascending sort by employee_number. Records
                            // without a number (the empNo helper returns
                            // "—") are pushed to the END. Numeric strings
                            // use natural sort so "2" < "10".
                            const raw = (v) => {
                              const s = String(empNo(v.user_id) || '').trim();
                              return (s === '' || s === '—' || s === '-') ? '' : s;
                            };
                            const na = raw(a);
                            const nb = raw(b);
                            if (!na && !nb) return 0;
                            if (!na) return 1;   // empty → end
                            if (!nb) return -1;  // empty → end
                            return na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
                          }).map((record) => (
                            <TableRow key={record.id} className="border-[#C4BFF0]" data-testid={`payroll-row-${record.id}`}>
                              <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(record.user_id)}</TableCell>
                              <TableCell><EmployeeName name={record.user_name} nameEn={record.user_name_en} badgeData={getBadge(record.user_id)} badgeLanguage={language} /></TableCell>
                              <TableCell className="text-[#655dc6]/80 font-medium" data-testid={`work-days-${record.id}`}>
                                {(() => {
                                  // v1.5.16: attendance_based mode
                                  // stores actual attended days.
                                  if (record.attendance_days_actual !== undefined && record.attendance_days_actual !== null) {
                                    const worked = Number(record.attendance_days_actual);
                                    const tt = language === 'ar'
                                      ? `أيام الحضور الفعلية (بصمة): ${worked}\nنمط الراتب: على الحضور فقط`
                                      : `Actual attended days (punched): ${worked}\nPayroll mode: pay-per-attendance`;
                                    return (
                                      <div
                                        className="cursor-help underline decoration-dotted decoration-[#655dc6]/40 underline-offset-2"
                                        title={tt}
                                      >
                                        {worked}
                                      </div>
                                    );
                                  }
                                  // v1.5.13: post-hire window is the ceiling,
                                  // not the raw cycle length. This makes
                                  // mid-cycle hires show 6, not 30.
                                  const total = record.worked_days_in_cycle || record.cycle_total_days || 0;
                                  const paidLeave = record.paid_leave_days || 0;
                                  const absent = (record.unpaid_leave_days || 0) + (record.unexcused_absence_days || 0);
                                  const worked = Math.max(0, total - absent - paidLeave);
                                  const tt = language === 'ar'
                                    ? `أيام الدورة الفعلية: ${total}\nأيام الإجازة المدفوعة: ${paidLeave}\nأيام الإجازة بدون راتب: ${record.unpaid_leave_days || 0}\nأيام الغياب غير المبرر: ${record.unexcused_absence_days || 0}\nأيام الحضور الفعلي: ${worked}`
                                    : `Effective cycle days: ${total}\nPaid leave days: ${paidLeave}\nUnpaid leave days: ${record.unpaid_leave_days || 0}\nUnexcused absences: ${record.unexcused_absence_days || 0}\nActual present days: ${worked}`;
                                  return (
                                    <div
                                      className="cursor-help underline decoration-dotted decoration-[#655dc6]/40 underline-offset-2"
                                      title={tt}
                                    >
                                      {worked}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-[#655dc6]/70">{record.month}/{record.year}</TableCell>
                              <TableCell className="text-[#655dc6]/70">
                                {record.basic_salary?.toLocaleString()}
                                {record.prorated && (
                                  <div
                                    className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50"
                                    title={
                                      language === 'ar'
                                        ? `الموظف باشر خلال الدورة (${record.worked_days_in_cycle} من ${record.salary_divisor || 30} يوم قياسي) — تحقق يدوياً. تاريخ الفعالية: ${record.effective_start_date}`
                                        : `Employee started mid-cycle (${record.worked_days_in_cycle} of ${record.salary_divisor || 30} standard days) — review manually. Effective start: ${record.effective_start_date}`
                                    }
                                    data-testid={`prorated-badge-${record.id}`}
                                  >
                                    <Warning size={10} weight="fill" />
                                    {language === 'ar' ? 'محسوب تناسبياً' : 'Prorated'}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-green-600">{(record.housing_allowance || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-green-600">{(record.transport_allowance || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-green-600">{(record.other_allowances || 0).toLocaleString()}</TableCell>
                              <TableCell className="bg-[#EDE9FE]/60 dark:bg-[#655dc6]/10 font-semibold text-[#655dc6] dark:text-[#B4A9F5]" data-testid={`gross-salary-${record.id}`}>
                                {(((record.basic_salary || 0) + (record.allowances || 0))).toLocaleString()}
                              </TableCell>
                              <TableCell className="text-blue-600 font-medium">
                                {(() => {
                                  const otAmt = record.overtime_amount || 0;
                                  if (otAmt === 0) return <span className="text-[#655dc6]/30">-</span>;
                                  const otHours = record.overtime_hours || 0;
                                  const otRate = record.overtime_rate || 0;
                                  const tt = language === 'ar'
                                    ? `عدد ساعات الإضافي المعتمدة: ${formatHoursHMLabeled(otHours, true)}\nالمبلغ: ${otAmt.toLocaleString()} ريال${otRate ? `\nالمعامل: ×${otRate}` : ''}`
                                    : `Approved OT hours: ${formatHoursHMLabeled(otHours, false)}\nAmount: ${otAmt.toLocaleString()} SAR${otRate ? `\nMultiplier: ×${otRate}` : ''}`;
                                  return (
                                    <div
                                      className="cursor-help underline decoration-dotted decoration-blue-400 underline-offset-2"
                                      title={tt}
                                      data-testid={`ot-detail-${record.id}`}
                                    >
                                      {otAmt.toLocaleString()}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              {/* Deductions split into 3 buckets: GOSI · Penalty · Admin */}
                              <TableCell className="text-rose-700">
                                {record.gosi_subscription > 0 ? (
                                  <span data-testid={`gosi-cell-${record.id}`}>-{record.gosi_subscription.toLocaleString()}</span>
                                ) : <span className="text-[#655dc6]/30">-</span>}
                              </TableCell>
                              <TableCell className="text-rose-700">
                                {(() => {
                                  const penalty = (record.late_penalty || 0)
                                    + (record.unpaid_leave_deduction || 0)
                                    + (record.unexcused_absence_deduction || 0)
                                    + (record.advance_paid_leave_deduction || 0);
                                  if (penalty === 0) return <span className="text-[#655dc6]/30">-</span>;
                                  const parts = [];
                                  if (record.late_penalty > 0) parts.push(`${language === 'ar' ? `تأخير (${record.late_minutes || 0} د)` : `Late (${record.late_minutes || 0} min)`}: ${record.late_penalty.toLocaleString()}`);
                                  if (record.unpaid_leave_deduction > 0) parts.push(`${language === 'ar' ? `إجازة بدون راتب (${record.unpaid_leave_days || 0} يوم)` : `Unpaid leave (${record.unpaid_leave_days || 0} d)`}: ${record.unpaid_leave_deduction.toLocaleString()}`);
                                  if (record.unexcused_absence_deduction > 0) parts.push(`${language === 'ar' ? `غياب بدون عذر (${record.unexcused_absence_days || 0} يوم)` : `Unexcused (${record.unexcused_absence_days || 0} d)`}: ${record.unexcused_absence_deduction.toLocaleString()}`);
                                  if (record.advance_paid_leave_deduction > 0) parts.push(`${language === 'ar' ? `إجازة مدفوعة مقدماً (${record.advance_paid_leave_days || 0} يوم)` : `Advance-paid leave (${record.advance_paid_leave_days || 0} d)`}: ${record.advance_paid_leave_deduction.toLocaleString()}`);
                                  // v1.6.13: Append per-day detail
                                  // from `penalty_breakdown` so finance
                                  // sees exactly which days are late
                                  // vs unexcused-absent in the tooltip.
                                  const breakdown = Array.isArray(record.penalty_breakdown)
                                    ? record.penalty_breakdown.filter(b => !b.waived && (b.amount || 0) > 0)
                                    : [];
                                  if (breakdown.length > 0) {
                                    parts.push(language === 'ar' ? '— الأيام:' : '— Days:');
                                    for (const b of breakdown) {
                                      const typeLbl = b.type === 'late'
                                        ? (language === 'ar' ? `تأخير ${b.minutes || 0}د` : `Late ${b.minutes || 0}m`)
                                        : (language === 'ar' ? 'غياب' : 'Absence');
                                      parts.push(`  • ${b.date} · ${typeLbl}: ${Number(b.amount || 0).toLocaleString()}`);
                                    }
                                  }
                                  const header = language === 'ar' ? 'تفاصيل الخصم الجزائي:' : 'Penalty breakdown:';
                                  return (
                                    <div
                                      className="cursor-help underline decoration-dotted decoration-rose-400 underline-offset-2"
                                      title={`${header}\n${parts.join('\n')}`}
                                      data-testid={`penalty-cell-${record.id}`}
                                    >
                                      -{penalty.toLocaleString()}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-rose-700">
                                {record.manual_deductions_total > 0 ? (
                                  (() => {
                                    const bd = record.manual_deductions_breakdown || [];
                                    const header = language === 'ar' ? 'تفاصيل الخصم الإداري:' : 'Admin deduction breakdown:';
                                    const parts = bd.length > 0
                                      ? bd.map((b) => `  • ${b.reason || (language === 'ar' ? 'خصم يدوي' : 'Manual')}: ${(b.amount || 0).toLocaleString()}`)
                                      : [`${language === 'ar' ? 'خصم يدوي' : 'Manual'}: ${record.manual_deductions_total.toLocaleString()}`];
                                    return (
                                      <div
                                        className="cursor-help underline decoration-dotted decoration-rose-400 underline-offset-2"
                                        title={`${header}\n${parts.join('\n')}`}
                                        data-testid={`admin-cell-${record.id}`}
                                      >
                                        -{record.manual_deductions_total.toLocaleString()}
                                      </div>
                                    );
                                  })()
                                ) : <span className="text-[#655dc6]/30">-</span>}
                              </TableCell>
                              <TableCell className="font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" data-testid={`net-salary-${record.id}`}>{record.net_salary?.toLocaleString()}</TableCell>
                              <TableCell>
                                {/* v1.5.28 — simplified to a single
                                    status badge: draft → sent. The
                                    internal pending/paid state is no
                                    longer exposed since "Mark as Paid"
                                    was removed. */}
                                {record.published_at ? (
                                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] px-2 py-0.5" data-testid={`status-${record.id}`}>
                                    {language === 'ar' ? 'مُرسَل ✓' : 'Sent ✓'}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-[10px] px-2 py-0.5" data-testid={`status-${record.id}`}>
                                    {language === 'ar' ? 'مسودة' : 'Draft'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-nowrap items-center justify-center gap-1 whitespace-nowrap">
                                {!record.published_at && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePublishOne(record)}
                                    disabled={publishingId === record.id}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-1.5 h-7 text-xs"
                                    data-testid={`send-one-btn-${record.id}`}
                                    title={language === 'ar' ? 'إرسال هذا الكشف فقط لهذا الموظف' : 'Send this payslip only to this employee'}
                                  >
                                    {publishingId === record.id ? (
                                      <SpinnerGap size={14} className="me-0.5 animate-spin" />
                                    ) : (
                                      <PaperPlaneTilt size={14} className="me-0.5" weight="fill" />
                                    )}
                                    {language === 'ar' ? 'إرسال' : 'Send'}
                                  </Button>
                                )}
                                {/* v1.5.24 — the "Mark as Paid" button
                                    was removed per finance-manager
                                    request. Once a payslip is sent
                                    ("إرسال"), employees always see it
                                    as "معمد" (approved) and there's no
                                    need for an internal pending/paid
                                    dance surfaced in the admin UI. */}
                                <Button variant="ghost" size="sm" onClick={() => handleDownloadPayslip(record.id)}
                                  className="text-[#655dc6] hover:bg-[#E8E6F5] px-1.5 h-7 text-xs" data-testid={`payslip-btn-${record.id}`}>
                                  <FileText size={14} className="me-0.5" /> {language === 'ar' ? 'كشف' : 'Slip'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteOne(record)}
                                  disabled={deletingId === record.id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 px-1.5 h-7"
                                  data-testid={`delete-payroll-${record.id}`}
                                  title={record.published_at
                                    ? (language === 'ar' ? 'حذف الكشف (تم إرساله للموظف)' : 'Delete (already sent)')
                                    : (language === 'ar' ? 'حذف المسودة' : 'Delete draft')}
                                >
                                  {deletingId === record.id
                                    ? <SpinnerGap size={14} className="animate-spin" />
                                    : <Trash size={14} />}
                                </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollShadow>
                      </div>
                      </>
                    );
                  })()}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== OVERTIME TAB ===== */}
          <TabsContent value="overtime">
            {activeTab === 'overtime' && (
              <OvertimeTab employees={employees} fetchEmployees={fetchEmployees} />
            )}
          </TabsContent>

          <TabsContent value="deductions">
            {activeTab === 'deductions' && (
              <DeductionsTab employees={employees} />
            )}
          </TabsContent>
        </Tabs>

        {/* Generate Payroll Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="sm:max-w-[400px]" data-testid="generate-payroll-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Money size={24} weight="duotone" className="text-[#655dc6]" />
                {t('generate_payroll')}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar'
                  ? 'سيتم إنشاء كشف الرواتب كمسودة قابلة للمراجعة. لن يُرسَل أي إشعار للموظفين إلا بعد ضغط "إرسال للموظفين" في الشاشة الرئيسية.'
                  : 'The payroll will be created as a reviewable draft. No employee notifications are sent until you press "Publish to Employees" on the main screen.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>{t('select_month')}</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{months.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('select_year')}</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map((y) => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>{t('cancel')}</Button>
              <Button onClick={handleGenerate} disabled={generating} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="confirm-generate-btn">
                {generating ? <SpinnerGap size={20} className="animate-spin" /> : t('generate_payroll')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══ v1.6.12: Advance-paid annual-leave picker ═════════════
            Shown after the admin confirms month/year WHEN there is at
            least one approved annual leave inside the cycle. Finance
            ticks every employee who already received his leave salary
            as a travel advance; only those are deducted from this
            payroll. Unchecked employees keep paying out the leave with
            the regular monthly salary (no deduction). Leave BALANCE is
            still debited for everyone — the toggle only moves cash. */}
        <Dialog
          open={!!annualLeavePreview}
          onOpenChange={(o) => { if (!o) setAnnualLeavePreview(null); }}
        >
          <DialogContent className="max-w-[640px]" data-testid="advance-paid-picker">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
                <Warning size={22} weight="duotone" />
                {language === 'ar' ? 'إجازات سنوية داخل الدورة' : 'Annual leaves inside the cycle'}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar'
                  ? 'أشّر على الموظف إذا كان راتب إجازته قد صُرف مقدماً (سلفة قبل السفر). الموظف المؤشَّر فقط ستُخصم أيام إجازته من راتب هذه الدورة. غير المؤشَّر يُدفع كالمعتاد.'
                  : 'Tick an employee if his leave salary was already paid as a travel advance. Only ticked employees will have their annual-leave days deducted from this cycle\u2019s salary.'}
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[380px] overflow-y-auto border rounded-md divide-y">
              {(annualLeavePreview?.employees || []).map((emp) => {
                const checked = advancePaidIds.has(emp.user_id);
                return (
                  <label
                    key={emp.user_id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F6FC] dark:hover:bg-[#2b2b3d]"
                    data-testid={`advance-paid-row-${emp.employee_number || emp.user_id}`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[#655dc6]"
                      checked={checked}
                      onChange={(e) => {
                        const next = new Set(advancePaidIds);
                        if (e.target.checked) next.add(emp.user_id);
                        else next.delete(emp.user_id);
                        setAdvancePaidIds(next);
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#2E2A5B] dark:text-[#DAD6FF] truncate">
                        {language === 'ar' ? (emp.name || emp.name_en) : (emp.name_en || emp.name)}
                        {emp.employee_number && (
                          <span className="text-xs text-[#655dc6]/60 mx-2">#{emp.employee_number}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#655dc6]/70">
                        {language === 'ar'
                          ? `${emp.total_days} يوم إجازة سنوية داخل الدورة`
                          : `${emp.total_days} annual-leave day(s) inside the cycle`}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <DialogFooter className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-[#655dc6]/70">
                {language === 'ar'
                  ? `المؤشَّرون: ${advancePaidIds.size} من ${(annualLeavePreview?.employees || []).length}`
                  : `Selected: ${advancePaidIds.size} / ${(annualLeavePreview?.employees || []).length}`}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAnnualLeavePreview(null)}>
                  {t('cancel')}
                </Button>
                <Button
                  onClick={() => _runGeneratePayroll(Array.from(advancePaidIds))}
                  disabled={generating}
                  className="bg-[#655dc6] hover:bg-[#544cb0]"
                  data-testid="confirm-advance-paid-btn"
                >
                  {generating ? <SpinnerGap size={18} className="animate-spin" /> : (language === 'ar' ? 'توليد المسير' : 'Generate')}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══ Publish preview modal ══════════════════════════════════
            Shown before the real publish call whenever the recompute
            pass detected differences. Lists every affected employee
            with net "before → after" so finance signs off on the
            adjustments before the emails go out. */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-[720px]" data-testid="publish-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
                <Warning size={24} weight="duotone" />
                {language === 'ar' ? 'مراجعة قبل الإرسال — تم إعادة حساب الغياب' : 'Review before publishing — absences recomputed'}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar'
                  ? 'تم رصد تغييرات في صافي راتب بعض الموظفين بناءً على أحدث بيانات الحضور. راجع القائمة ثم اضغط "تأكيد الإرسال" لإصدار الكشوف.'
                  : 'Some payslips changed after re-checking the latest attendance. Review the list then click "Confirm & Send" to release them.'}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-[#EDE9FE]">
              <Table className="[&_th]:text-center [&_td]:text-center">
                <TableHeader className="sticky top-0 bg-[#F5F4FC] z-10">
                  <TableRow>
                    <TableHead>{language === 'ar' ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{language === 'ar' ? 'أيام غياب' : 'Absent days'}</TableHead>
                    <TableHead>{language === 'ar' ? 'صافي قبل' : 'Net (before)'}</TableHead>
                    <TableHead>{language === 'ar' ? 'صافي بعد' : 'Net (after)'}</TableHead>
                    <TableHead>{language === 'ar' ? 'الفرق' : 'Δ'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(previewData?.recomputed || []).map((r) => {
                    const before = Number(r.diff?.net_salary?.before || 0);
                    const after = Number(r.diff?.net_salary?.after || 0);
                    const delta = after - before;
                    const absBefore = r.diff?.unexcused_absence_days?.before ?? 0;
                    const absAfter = r.diff?.unexcused_absence_days?.after ?? 0;
                    return (
                      <TableRow key={r.payroll_id} data-testid={`preview-row-${r.payroll_id}`}>
                        <TableCell className="font-medium">
                          {language === 'ar' ? r.user_name : (r.user_name_en || r.user_name)}
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-500">{absBefore}</span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold text-[#655dc6]">{absAfter}</span>
                        </TableCell>
                        <TableCell className="text-gray-500 line-through">{before.toLocaleString()}</TableCell>
                        <TableCell className="font-bold text-emerald-700 bg-emerald-50">{after.toLocaleString()}</TableCell>
                        <TableCell className={delta < 0 ? 'text-red-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                          {delta > 0 ? '+' : ''}{delta.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setPreviewOpen(false); setPreviewData(null); }}
                data-testid="preview-cancel-btn" disabled={publishing}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={confirmPublishFromPreview}
                disabled={publishing}
                className="bg-[#655dc6] hover:bg-[#544cb0]"
                data-testid="preview-confirm-btn"
              >
                {publishing
                  ? <SpinnerGap size={18} className="animate-spin me-2" />
                  : <PaperPlaneTilt size={18} className="me-2" weight="fill" />}
                {language === 'ar'
                  ? `تأكيد الإرسال (${previewData?.recomputed?.length || 0})`
                  : `Confirm & Send (${previewData?.recomputed?.length || 0})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Salary Adjustment Dialog — supplemental payslip issuance */}
        <SalaryAdjustmentDialog
          open={showAdjustmentDialog}
          onOpenChange={setShowAdjustmentDialog}
          employees={employees}
          language={language}
          direction={direction}
          onSuccess={() => { fetchPayroll(); fetchEmployees(); }}
        />

        <AttendanceCorrectionDialog
          open={showAttCorrectionDialog}
          onOpenChange={setShowAttCorrectionDialog}
          employees={employees}
          language={language}
          direction={direction}
          onSuccess={() => { fetchPayroll(); }}
        />
      </main>
    </div>
  );
}
